import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import UsersTable from './UsersTable';
import UserCreateForm from './UserCreateForm';
import UserEditModal from './UserEditModal';

const ROLE_FILTERS = [
  { value: '', label: 'Todos' },
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'readonly', label: 'Readonly' },
  { value: 'member', label: 'Member (App)' },
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = roleFilter ? `?role=${roleFilter}` : '';
      const response = await api.get(`/api/v1/users${params}`);
      setUsers(response.data.data);
      setTotal(response.data.meta.total);
    } catch (err) {
      if (err.response?.status === 403) {
        showError('No tenés permisos para ver la lista de usuarios.');
      } else {
        showError('Error al cargar los usuarios.');
      }
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Crear usuario ──────────────────────────────────────────────────────────

  const handleCreate = async (formData) => {
    setActionLoading(true);
    try {
      await api.post('/api/v1/users', formData);
      showSuccess(`Usuario ${formData.email} creado correctamente.`);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 409) {
        showError('Ya existe un usuario con ese email.');
      } else if (err.response?.status === 403) {
        showError('Solo el superadmin puede crear usuarios.');
      } else {
        showError('Error al crear el usuario.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Editar usuario ─────────────────────────────────────────────────────────

  const handleSaveEdit = async (userId, updates) => {
    setActionLoading(true);
    try {
      await api.put(`/api/v1/users/${userId}`, updates);
      showSuccess('Usuario actualizado correctamente.');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 400) {
        showError(err.response.data?.detail || 'Operación no permitida.');
      } else if (err.response?.status === 403) {
        showError('Solo el superadmin puede editar usuarios.');
      } else {
        showError('Error al actualizar el usuario.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ── Eliminar usuario ───────────────────────────────────────────────────────

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `¿Eliminar permanentemente a ${user.display_name} (${user.email})?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await api.delete(`/api/v1/users/${user.id}`);
      showSuccess(`Usuario ${user.email} eliminado.`);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 400) {
        showError(err.response.data?.detail || 'Operación no permitida.');
      } else if (err.response?.status === 403) {
        showError('Solo el superadmin puede eliminar usuarios.');
      } else {
        showError('Error al eliminar el usuario.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h4 style={{ margin: 0 }}>Usuarios</h4>
        <span style={{ color: '#888', fontSize: '0.9rem' }}>
          {total} usuario{total !== 1 ? 's' : ''} en total
        </span>
      </div>

      {/* Mensajes de feedback */}
      {error && (
        <div className="alert alert-danger" style={{ borderRadius: '6px' }}>{error}</div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ borderRadius: '6px' }}>{successMsg}</div>
      )}

      {/* Formulario crear usuario */}
      <UserCreateForm onSubmit={handleCreate} loading={actionLoading} />

      {/* Filtros por rol */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {ROLE_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            className="btn btn-sm"
            onClick={() => setRoleFilter(value)}
            style={{
              backgroundColor: roleFilter === value ? '#C73B33' : '#f0f0f0',
              color: roleFilter === value ? '#fff' : '#333',
              border: 'none',
              borderRadius: '20px',
              padding: '4px 14px',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tabla */}
      {loading
        ? <LoadingSpinner />
        : <UsersTable users={users} onEdit={setEditingUser} onDelete={handleDelete} />
      }

      {/* Modal edición */}
      <UserEditModal
        user={editingUser}
        onSave={handleSaveEdit}
        onClose={() => setEditingUser(null)}
        loading={actionLoading}
      />
    </div>
  );
};

export default Users;
